// config
// api
// utils
// type
import activityWhatsAppApi from '@/api/activityWhatsApp';
import {
  CampaignStatus,
  type Campaign,
  type CreateCampaignParams,
} from '@/api/activityWhatsApp/data.d';
import { StoreKeyEnum } from '@/definition/common/data.d';
import type { TdFormRule } from '@/definition/common/tdesign.d';
import { intlGetApiError } from '@/locales';
import type { BuiltInSegmentItemType } from '@/pages/activityManage/create/data';
import { ActivityWhatsAppEventNameEnum } from '@/pages/activityWhatsApp/utils/enum/create';
import { onHandleFilters } from '@/pages/segmentsManage/utils/const/addOrEdit';
import { HandleDataType } from '@/pages/segmentsManage/utils/typings/addOrEdit';
import { EventTypeEnum } from '@/utils/aegisMonitor/typing';
import burialPoint from '@/utils/burialPoint';
import { debounce } from '@/utils/common';
import { getLocalStorage } from '@/utils/global';
import moment from 'moment';
import type { FormDataType } from '../../create/data.d';
import { DeliverTypeEnum, ToTabsEnum } from '../../create/data.d';
/** whatsapp活动名称验证重复 */
const whatsAppNameValidator = debounce(
  async ({
    name,
    getCheckFlag,
    originalName,
    callBack,
  }: {
    name: string;
    uuid?: string;
    getCheckFlag: (res: any) => boolean;
    originalName?: string;
    callBack: (result: any) => void;
  }) => {
    let returnParams = {
      result: true,
      message: '',
      type: 'success',
      trigger: '',
    };

    const errorParams = {
      result: false,
      message: intlGetApiError('2000'),
      type: 'error',
      trigger: 'blur',
    };

    if (originalName && originalName === name) {
      return callBack(returnParams);
    }
    try {
      const result: any = await activityWhatsAppApi.checkName(name?.trim());
      const flag = getCheckFlag(result);
      if (flag) {
        burialPoint.infoAll({
          msg: `${name?.trim()}活动名称已经存在`,
          ext1: '活动名称校验',
          moduleName: 'activityWhatsApp',
          eventName:
            ActivityWhatsAppEventNameEnum.ACTIVITY_NAME_VALIDATION_DUPLICATE,
          eventType: EventTypeEnum.ERROR,
        });
        returnParams = errorParams;
      } else {
        burialPoint.infoAll({
          msg: `${name?.trim()}活动名称校验通过`,
          ext1: '活动名称校验通过',
          moduleName: 'activityWhatsApp',
          eventName:
            ActivityWhatsAppEventNameEnum.ACTIVITY_NAME_VALIDATION_SUCCESS,
          eventType: EventTypeEnum.CLICK,
        });
      }
    } catch {
      burialPoint.infoAll({
        msg: `${name?.trim()}活动名称校验错误`,
        ext1: '活动名称校验',
        moduleName: 'activityWhatsApp',
        eventName: ActivityWhatsAppEventNameEnum.ACTIVITY_NAME_VALIDATION_ERROR,
        eventType: EventTypeEnum.ERROR,
      });
      returnParams = errorParams;
    }
    callBack(returnParams);
  },
  300,
);
// 活动名称校验规则
export const whatsAppNameRule = (params?: {
  originalName?: string;
}): TdFormRule[] => {
  return [
    {
      required: true,
      messageKey: 'ACTIVITY.MANAGE.LIST.CREATE.PLEASE.ENTER.ACTIVITY.NAME',
      type: 'error',
    },
    {
      whitespace: true,
      messageKey: 'ACTIVITY.MANAGE.LIST.CREATE.PLEASE.ENTER.ACTIVITY.NAME',
      type: 'error',
    },
    {
      validator: (name: string) => {
        if (!name?.trim()) return false;
        return new Promise((resolve) => {
          whatsAppNameValidator({
            ...params,
            name,
            getCheckFlag: (res: any) => res.data?.exists,
            callBack: (result: any) => {
              resolve(result);
            },
          });
        });
      },
    },
  ];
};

// 默认表单
export const defaultForm: FormDataType = {
  name: '',
  whatsAppAccountName: '',
  senderPhoneNumber: '',
  whatsAppAccountId: 0,
  segmentType: ToTabsEnum.default,
  segmentName: '',
  defaultLanguage: 'en',
  contentTemplate: [],
  deliverType: DeliverTypeEnum.userLocation,
  deliverTime: '',
  status: CampaignStatus.DRAFT,
};

// 字段映射处理函数
export const fieldProcessors: Record<
  string,
  (
    sourceData: Campaign,
    targetData: FormDataType,
    isEdit: boolean,
    isCopy: boolean,
    draftCache: any,
    setOriginalName: (name: string) => void,
  ) => void
> = {
  // 处理名称
  name: (
    sourceData,
    targetData,
    isEdit,
    isCopy,
    draftCache,
    setOriginalName,
  ) => {
    if (isEdit) {
      setOriginalName(sourceData.name);
    }
    targetData.name = sourceData.name;
    if (isCopy) {
      const newName = draftCache.name || `${sourceData.name}_copy`;
      targetData.name = newName.slice(0, 50);
    }
  },
  whatsAppAccount: (sourceData, targetData) => {
    const { whatsAppAccount } = sourceData;
    if (whatsAppAccount) {
      targetData.whatsAppAccountId = whatsAppAccount.id;
      targetData.whatsAppAccountName = whatsAppAccount.whatsAppAccountName;
      targetData.senderPhoneNumber = whatsAppAccount.senderPhoneNumber;
    }
  },
  // 处理内容模板
  contentTemplate: (sourceData, targetData) => {
    targetData.contentTemplate = sourceData.createTaskParamsList || [];
  },
  // 处理分组类型和数据
  segmentType: (sourceData, targetData) => {
    if (sourceData.segmentType) {
      targetData.segmentType = sourceData.segmentType;
      targetData.segmentTo = `${sourceData.segmentType}-${
        sourceData.segmentId || sourceData.builtInSegmentUuid
      }`;
    }
  },
  // 处理投放时间格式
  deliverTime: (sourceData, targetData) => {
    if (sourceData.deliverTime) {
      targetData.deliverTime = moment(sourceData.deliverTime).format(
        'YYYY-MM-DD HH:mm',
      );
    }
  },
  // 处理开始时间格式
  startTime: (sourceData, targetData) => {
    if (sourceData.startTime) {
      targetData.startTime = moment(sourceData.startTime).format(
        'YYYY-MM-DD HH:mm',
      );
    }
  },
};

export const handSubmitDeliverTime = (
  deliverTime: string,
  deliverType: DeliverTypeEnum,
) => {
  if (deliverType === DeliverTypeEnum.userLocation) {
    const date = new Date(deliverTime);

    // 获取时区偏移量
    const currentTimezoneOffset = -new Date().getTimezoneOffset() / 60;

    date.setHours(date.getHours() + currentTimezoneOffset);
    return date.toISOString();
  }
  return new Date(deliverTime).toISOString();
};
// 时间格式化函数
export const formatDeliverTimeLocal = (deliverTime?: string) => {
  return deliverTime ? moment(deliverTime).format('YYYY-MM-DD HH:mm:ss') : null;
};
// 组装提交数据
export const handSubmitData = (
  data: FormDataType,
  campaignData: Campaign | null,
  isCopy: boolean,
  segmentFilterJson: any,
) => {
  // 是否已经选择模板
  const isSelectedTemplate = data.contentTemplate?.length > 0;
  // 选择了模板并且选择了时间
  const isSelectedDeliverTime = data.deliverTime && isSelectedTemplate;
  const newFormData: CreateCampaignParams & { id?: number } = {
    name: data.name,
    whatsAppAccountId: data.whatsAppAccountId!,
    segmentId:
      data.segmentType === ToTabsEnum.builtinSegment ? 0 : data.segmentId,
    segmentType: data.segmentType,
    segmentName: data.segmentName,
    deliverType: isSelectedTemplate ? data.deliverType : DeliverTypeEnum.fixed,
    deliverTime: isSelectedDeliverTime
      ? new Date(data.deliverTime || 0).toISOString()
      : null,
    deliverTimeLocal: formatDeliverTimeLocal(data.deliverTime),
    defaultLanguage: data.defaultLanguage,
    createTaskParamsList: isSelectedTemplate ? data.contentTemplate : null,
    totalUserCount: data.totalUserCount,
    builtInSegmentList:
      data.builtInSegmentList?.map((item) => ({
        uuid: item.uuid,
        filters: onHandleFilters(
          (item as BuiltInSegmentItemType).filters || [],
          {
            disabled: false,
            optType: HandleDataType.SUBMIT,
            currentTimezone:
              getLocalStorage(StoreKeyEnum.LOCAL_TIMEZONE) || 'UTC+8:00',
            filterJson: segmentFilterJson,
          },
        ),
        relation: item.relation,
        name: item.name,
        group: item.group,
        value: item.value,
      })) ?? null,
    builtInSegmentUuid: data.builtInSegmentUuid || null,
  };
  let api;
  if (campaignData?.id && !isCopy) {
    newFormData.id = campaignData.id;
    api = activityWhatsAppApi.update;
  } else {
    api = activityWhatsAppApi.create;
  }

  return { newFormData, api };
};

// 牛信云互转smartlink语言代码 默认是牛信云转sml
const languageMap = {
  ar: 'ar',
  cs: 'cs',
  de: 'de',
  en: 'en',
  en_GB: 'en-GB',
  en_US: 'en-US',
  es_AR: 'es',
  es_ES: 'es',
  es_MX: 'es-MX',
  fil: 'fil-PH',
  fi: 'fi',
  fr: 'fr',
  hi: 'hi',
  id: 'id',
  it: 'it',
  ja: 'ja',
  ko: 'ko',
  ms: 'ms',
  nl: 'nl',
  pl: 'pl',
  pt_BR: 'pt-BR',
  pt_PT: 'pt',
  ru: 'ru',
  th: 'th',
  tr: 'tr',
  ur: 'ur',
  uz: 'uz',
  vi: 'vi',
  zh_CN: 'zh-Hans',
  zh_HK: 'zh-Hant',
  zh_TW: 'zh-TW',
  es: 'es', // 保持es在最底部，在sml转牛信云时，es会覆盖前面重复的es属性
};

// 交换对象的键和值
const swapKeysAndValues = (obj: any) => {
  return Object.entries(obj).reduce((acc: any, [key, value]: any) => {
    acc[value] = key;
    return acc;
  }, {});
};

export const nxcloudToSmartlinkLanguage = (
  language: string,
  isSwap = false,
) => {
  const mapObj = isSwap ? swapKeysAndValues(languageMap) : languageMap;
  return mapObj[language];
};

export const getBurialPointMsg = (
  detailData: FormDataType & { action: string },
) => {
  return `活动名称:${detailData.name}，操作类型:${
    detailData.action
  }，活动状态:${detailData.status}，操作时间:${moment().format(
    'YYYY-MM-DD HH:mm:ss',
  )}，发送账号名称:${detailData.whatsAppAccountName}，发送账号ID:${
    detailData.whatsAppAccountId
  }，发送手机号:${detailData.senderPhoneNumber}，分组类型:${
    detailData.segmentType
  }，分组名称:${detailData.segmentName}，分组ID:${
    detailData.segmentId
  }，内容模板:${(detailData.contentTemplate || [])
    .map((item) => item?.templateName)
    .join(',')}，发送时间:${detailData.deliverTime}，发送时间类型:${
    detailData.deliverType
  }`;
};
